import * as THREE from 'three';
import type { Tile } from '../textures.ts';

/**
 * A tiny non-indexed mesh builder.
 *
 * Three's own generators fight us on two counts: they spread one UV rect across
 * a whole face (so a texture cannot repeat without a second material), and they
 * share vertices between quads (so per-quad UVs are impossible). Building the
 * triangles by hand is less code than working around either.
 *
 * Vertex counts are irrelevant at this scale - the entire diorama is a few
 * thousand triangles - and non-indexed geometry merges without fuss.
 */

export type Vec3 = [number, number, number];

/**
 * Per-face brightness, standing in for a lighting rig we do not have. Kept in a
 * fairly tight range: a station is lit from every direction by strip lights, so
 * deep shadow on the side faces reads as wrong rather than as dramatic.
 */
const FACE_SHADE = {
  py: 1.0, // up: brightest
  ny: 0.45, // down
  px: 0.9,
  nx: 0.78,
  pz: 0.85,
  nz: 0.72,
} as const;

export type Face = keyof typeof FACE_SHADE;

export interface FaceTiles {
  px?: Tile;
  nx?: Tile;
  py?: Tile;
  ny?: Tile;
  pz?: Tile;
  nz?: Tile;
  all?: Tile;
  sides?: Tile;
}

export class MeshBuilder {
  private positions: number[] = [];
  private uvs: number[] = [];
  private colors: number[] = [];

  /**
   * One quad, wound counter-clockwise from p0. `tile` supplies the UV rect;
   * `repeat` tiles it across the quad rather than stretching it.
   *
   * The quad is subdivided into one sub-quad per repetition, each with UVs
   * confined to the tile's rect. That subdivision is the whole point: a texture
   * atlas cannot use GL's wrap modes, because UVs outside the rect land on a
   * neighbouring tile rather than wrapping back to the start of this one. An
   * earlier version simply scaled the UVs up by the repeat count, which quietly
   * panned across the atlas and drew slices of unrelated tiles.
   */
  quad(
    p0: Vec3,
    p1: Vec3,
    p2: Vec3,
    p3: Vec3,
    tile: Tile,
    color: THREE.Color,
    repeat: [number, number] = [1, 1],
  ): this {
    const ru = Math.max(repeat[0], 1e-6);
    const rv = Math.max(repeat[1], 1e-6);
    const cols = Math.max(1, Math.ceil(ru - 1e-6));
    const rows = Math.max(1, Math.ceil(rv - 1e-6));

    // Bilinear interpolation across all four corners, with s running p0->p1 and
    // t running p0->p3. Using every corner rather than two edge vectors means
    // this holds for any quad, not just the parallelograms box() happens to feed
    // it - and adjacent sub-quads share exactly computed edges, so no seams.
    const at = (s: number, t: number): Vec3 => {
      const w0 = (1 - s) * (1 - t);
      const w1 = s * (1 - t);
      const w2 = s * t;
      const w3 = (1 - s) * t;
      return [
        p0[0] * w0 + p1[0] * w1 + p2[0] * w2 + p3[0] * w3,
        p0[1] * w0 + p1[1] * w1 + p2[1] * w2 + p3[1] * w3,
        p0[2] * w0 + p1[2] * w1 + p2[2] * w2 + p3[2] * w3,
      ];
    };

    for (let col = 0; col < cols; col++) {
      // The last repetition is usually partial; it gets a partial slice of the
      // tile rather than a squashed whole one.
      const uEnd = Math.min(col + 1, ru);
      const s0 = col / ru;
      const s1 = uEnd / ru;
      const uSpan = uEnd - col;

      for (let row = 0; row < rows; row++) {
        const vEnd = Math.min(row + 1, rv);
        const t0 = row / rv;
        const t1 = vEnd / rv;
        const vSpan = vEnd - row;

        const a = at(s0, t0);
        const b = at(s1, t0);
        const c = at(s1, t1);
        const d = at(s0, t1);
        this.tri(a, b, c, [0, 0], [uSpan, 0], [uSpan, vSpan], tile, color);
        this.tri(a, c, d, [0, 0], [uSpan, vSpan], [0, vSpan], tile, color);
      }
    }
    return this;
  }

  private tri(
    a: Vec3,
    b: Vec3,
    c: Vec3,
    uvA: [number, number],
    uvB: [number, number],
    uvC: [number, number],
    tile: Tile,
    color: THREE.Color,
  ) {
    const du = tile.u1 - tile.u0;
    const dv = tile.v1 - tile.v0;
    for (const [p, uv] of [[a, uvA], [b, uvB], [c, uvC]] as const) {
      this.positions.push(p[0], p[1], p[2]);
      this.uvs.push(tile.u0 + uv[0] * du, tile.v0 + uv[1] * dv);
      this.colors.push(color.r, color.g, color.b);
    }
  }

  /**
   * An axis-aligned box. `tileWorld` is the world size one texture tile should
   * cover; surfaces larger than that repeat it instead of stretching.
   */
  box(
    center: Vec3,
    size: Vec3,
    tiles: FaceTiles,
    tint: THREE.ColorRepresentation = 0xffffff,
    tileWorld = 0,
  ): this {
    const [cx, cy, cz] = center;
    const [w, h, d] = size;
    const x0 = cx - w / 2;
    const x1 = cx + w / 2;
    const y0 = cy - h / 2;
    const y1 = cy + h / 2;
    const z0 = cz - d / 2;
    const z1 = cz + d / 2;

    const base = new THREE.Color(tint);
    const faceColor = (face: Face) => base.clone().multiplyScalar(FACE_SHADE[face]);
    const rep = (a: number, b: number): [number, number] =>
      tileWorld > 0 ? [Math.max(1, a / tileWorld), Math.max(1, b / tileWorld)] : [1, 1];

    const pick = (face: Face): Tile | undefined => {
      const direct = tiles[face];
      if (direct) return direct;
      if (face !== 'py' && face !== 'ny' && tiles.sides) return tiles.sides;
      return tiles.all;
    };

    const faces: [Face, Vec3, Vec3, Vec3, Vec3, [number, number]][] = [
      ['px', [x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], rep(d, h)],
      ['nx', [x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], rep(d, h)],
      ['py', [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], rep(w, d)],
      ['ny', [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], rep(w, d)],
      ['pz', [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], rep(w, h)],
      ['nz', [x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], rep(w, h)],
    ];

    for (const [face, p0, p1, p2, p3, repeat] of faces) {
      const tile = pick(face);
      if (!tile) continue;
      this.quad(p0, p1, p2, p3, tile, faceColor(face), repeat);
    }
    return this;
  }

  get isEmpty(): boolean {
    return this.positions.length === 0;
  }

  build(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(this.uvs, 2));
    // Named aColor rather than color: three reserves `color` for its own
    // vertex-colour plumbing, which we are deliberately not using.
    geometry.setAttribute('aColor', new THREE.Float32BufferAttribute(this.colors, 3));
    return geometry;
  }
}
